/**
 * The placeholder a stat card shows when the read behind it FAILED.
 *
 * Deliberately not `'--'`, which every analytics card already uses for "still
 * loading", and deliberately not `'0'`, which is what all seven of them printed
 * before run 97: `Boolean(data) ? data.value : isLoading ? '--' : '0'` sends a
 * refused read (`data === undefined`, `isLoading === false`) straight to the
 * last arm, so a venue that made €8,432 and a venue whose revenue read was
 * refused both reported `0,00 €`.
 *
 * An em dash on its own would still be ambiguous next to `--`, so it never
 * appears alone: `MiniStatistics` renders the `isError` pill beside it, and the
 * two together are what distinguish "unknown" from "loading" and from "zero".
 */
export const UNKNOWN_STAT_VALUE = '—';

/**
 * True when a read has failed AND has nothing to show.
 *
 * The `!data` half is not defensive padding — it is the rule this campaign got
 * wrong once and has re-derived on every slice since (R96-3). React Query keeps
 * the last good `data` when a BACKGROUND refetch fails and raises `isError`
 * beside it; a card that reads the flag alone would replace a correct revenue
 * figure with "unknown" because a 30-second refresh failed, which is the mirror
 * of the defect being fixed.
 *
 * CALLER SET WIDENED (run 101) — re-reviewed, unchanged. This was a stat-card
 * helper reading `{ value: number }` payloads; it is now also the first branch
 * of the shared `classifyRead` in `features/_shared/readState.ts`, which runs
 * over a navbar dropdown, a home-screen panel and a command-palette search
 * whose payloads are objects and arrays. The widening is safe because the
 * function never inspects the SHAPE of `data` — it is typed `unknown` and only
 * compared against `undefined`/`null` — so an empty array or an object with
 * zero rows is `data`, not absence, which is exactly what the new callers need
 * (a resolved read with no notifications must reach their empty state, not
 * their error state). Recorded rather than assumed because a helper's blast
 * radius is a property of who calls it, and nothing in the diff that adds a
 * caller shows the other eleven.
 */
export function isUnreadable(isError: boolean, data: unknown): boolean {
    return isError && (data === undefined || data === null);
}

/**
 * The placeholder a stat card shows while its read is genuinely IN FLIGHT.
 *
 * Named rather than inlined because the difference between this and
 * `UNKNOWN_STAT_VALUE` above is the whole of R100-1: `--` is a promise that a
 * number is coming, and it must never be shown for a read that was never
 * issued.
 */
export const LOADING_STAT_VALUE = '--';

/**
 * What a stat card renders when it has no value: `--` while the read is really
 * in flight, and the unknown placeholder otherwise.
 *
 * The "otherwise" is the case every analytics card got wrong until run 100, and
 * it is not an error case — it is a read that was never ISSUED. All seven cards
 * wrote the three-way choice as
 *
 *     Boolean(data) ? data.value : isLoading ? '--' : '0'
 *
 * and React Query v4 reports a DISABLED query as `isLoading: true` with
 * `fetchStatus: 'idle'`, so on `/analytics` — which renders every card whether
 * or not a location is selected — an operator with no location saw `--` in
 * every card forever. Flipping the flag alone would have been worse than the
 * defect: with `isLoading` false the same expression falls to the LAST arm and
 * prints a real `0`, which is the exact lie run 97 spent a slice removing.
 * Hence a placeholder rather than a flag change: no data and not loading means
 * we do not know, whatever the reason.
 *
 * @param isInitialLoading the query's own `isInitialLoading` — `isLoading &&
 *   isFetching`, so it is false for a disabled query. Prefer it to the
 *   `isReadPending` predicate below wherever a call site has the query result
 *   in hand: `isReadPending` asks the CALLER to restate the hook's `enabled`
 *   expression, and a caller that restates it incompletely (a hook gated on
 *   `!!organizationId && !!sport` called with `Boolean(organizationId)`) is
 *   wrong in the direction that spins forever, with nothing to catch it.
 */
export function statPlaceholder(isInitialLoading: boolean): string {
    return isInitialLoading ? LOADING_STAT_VALUE : UNKNOWN_STAT_VALUE;
}

/**
 * True when a read is genuinely still in flight — as opposed to DISABLED, or
 * already finished by failing.
 *
 * Every analytics and impact query in this feature is gated
 * (`enabled: !!locationId`, `enabled: !!organizationId`), and React Query v4
 * reports a disabled query as `isLoading: true` with `fetchStatus: 'idle'`.
 * So a spinner or a skeleton driven by `isLoading` alone runs FOREVER whenever
 * no location is selected — the operator's home screen showed three skeletons
 * that never resolved — and a chart driven by `!data && isLoading` shows its
 * loading affordance on a state that will never produce data.
 *
 * The `!isError` half is the same rule one flag over: a failed read is not
 * loading, which is the assumption that let a refused read fall through every
 * card in this feature to a real `0` (run 97) and every chart to an empty
 * series (run 99).
 *
 * @param isEnabled whether the query's own `enabled:` predicate is satisfied —
 *                  pass the same expression the hook is gated on.
 *
 * NOTE (run 100): React Query v4.36 exposes `isInitialLoading` — `isLoading &&
 * isFetching` — which answers the same question without asking the caller to
 * restate the hook's gate, and is therefore the better instrument wherever the
 * query result is in hand (see `statPlaceholder`). This predicate stays for the
 * run-99 call sites that drive a JSX affordance and for any site that must
 * reason about a gate it computes itself; new code should reach for
 * `isInitialLoading` first. The two agree by construction: a disabled query is
 * never fetching, and a query that has failed is no longer loading.
 */
export function isReadPending(isEnabled: boolean, isError: boolean, isLoading: boolean): boolean {
    return isEnabled && !isError && isLoading;
}
